// Comprehensive error handling utilities

export class CadifyError extends Error {
  constructor(message, type = 'GENERAL', details = null) {
    super(message);
    this.name = 'CadifyError';
    this.type = type;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

export const ErrorTypes = {
  FILE_LOAD: 'FILE_LOAD',
  FILE_PARSE: 'FILE_PARSE',
  GEOMETRY: 'GEOMETRY',
  EXPORT: 'EXPORT',
  VALIDATION: 'VALIDATION',
  MEMORY: 'MEMORY',
  GENERAL: 'GENERAL'
};

export const handleError = (error, context = '') => {
  console.error(`[Cadify Error] ${context}:`, error);
  
  let userMessage = 'An unexpected error occurred.';
  
  if (error instanceof CadifyError) {
    userMessage = error.message;
  } else if (error.name === 'TypeError') {
    userMessage = 'Invalid data format. Please check your file.';
  } else if (error.name === 'RangeError') {
    userMessage = 'File too large or invalid dimensions.';
  } else if (error.message) {
    userMessage = error.message;
  }
  
  return {
    message: userMessage,
    type: error.type || ErrorTypes.GENERAL,
    details: error.details || error.stack,
    timestamp: new Date().toISOString()
  };
};

export const validateFile = (file, allowedExtensions) => {
  if (!file) {
    throw new CadifyError('No file provided', ErrorTypes.VALIDATION);
  }
  
  const ext = file.name.split('.').pop().toLowerCase();
  if (!allowedExtensions.includes(ext)) {
    throw new CadifyError(
      `Unsupported file format: .${ext}. Supported formats: ${allowedExtensions.join(', ')}`,
      ErrorTypes.VALIDATION
    );
  }
  
  // Check file size (max 100MB)
  const maxSize = 100 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new CadifyError(
      `File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size: 100MB`,
      ErrorTypes.VALIDATION
    );
  }
  
  return true;
};

export const validateGeometry = (geometry) => {
  if (!geometry) {
    throw new CadifyError('Invalid geometry: null or undefined', ErrorTypes.GEOMETRY);
  }
  
  if (!geometry.attributes || !geometry.attributes.position) {
    throw new CadifyError('Invalid geometry: missing position attribute', ErrorTypes.GEOMETRY);
  }
  
  const posCount = geometry.attributes.position.count;
  if (posCount === 0) {
    throw new CadifyError('Invalid geometry: no vertices', ErrorTypes.GEOMETRY);
  }
  
  if (posCount > 10000000) {
    throw new CadifyError(
      `Geometry too complex: ${posCount} vertices. Consider simplifying the model.`,
      ErrorTypes.GEOMETRY
    );
  }
  
  return true;
};

export const safeExecute = async (fn, errorContext = '') => {
  try {
    return await fn();
  } catch (error) {
    return { error: handleError(error, errorContext) };
  }
};

